/**
 * Спринты, используются для задач. Модуль для работы с API
 * @module sprints
 */

import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получение списка спринтов. Поддерживает пагинацию, поиск и фильтры
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список спринтов
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params, isAdmin) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/sprints${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка спринтов без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список спринтов
 * @throws {Error} - ошибка при получении данных
 */
export const getSprintsOptions = async (params, isAdmin) => {
  const searchParams = new URLSearchParams();

  Object.entries({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        searchParams.append(`${key}[]`, v);
      });
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();

  try {
    const response = await fetch(
      `/api/sprints${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.sprints;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает спринт по UUID
 * @param {string} uuid - UUID спринта
 * @returns {Promise} - спринт
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid, isAdmin) => {
  try {
    const response = await fetch(
      `/api/sprint${isAdmin ? "/admin" : ""}?uuid=${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${
          JSON.stringify(data.error) || "Обновите страницу"
        }`
      );
    }

    return data.sprint;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание спринта
 * @param {Object} item - Данные спринта
 * @returns {Promise} - созданный спринт
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/sprint", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.sprint;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление спринта
 * @param {Object} item - Данные спринта
 * @returns {Promise} - обновленный спринт
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/sprint", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.sprint;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление спринта
 * @param {Object} item - Данные спринта
 * @returns {Promise} - удаленный спринт
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/sprint", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.sprint;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавление списка задач в спринт
 * @param {Object} data - Данные задач и спринта (uuids)
 * @returns {Promise} - обновленный спринт
 * @throws {Error} - ошибка при обновлении данных
 */
export const addTasks = async (data) => {
  try {
    const response = await fetch("/api/sprint/tasks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const res = await response.json();

    if (!response.ok || res.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${res.error || "Попробуйте снова"}`
      );
    }

    return res;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка задач, которые можно добавить в конкретный спринт
 * @param {Object} params - Параметры запроса {uuid: sprintUuid} - обязательно
 * @returns {Promise} - список сзадач
 * @throws {Error} - ошибка при получении данных
 */
export const getTasksList = async (params, isAdmin) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(
      `/api/sprint/tasks/available${isAdmin ? "/admin" : ""}?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};
